import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  type User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, IS_FIREBASE_CONFIGURED } from '../config/firebase';

interface AuthContextValue {
  user: FirebaseUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Minimal fake user used when Firebase credentials are not configured (no web/.env).
 * Keeps the app functional for local development without a Firebase project.
 * Cast is safe here — only uid and email are accessed by the app.
 */
const DEV_USER = {
  uid: 'dev-local',
  email: 'dev@local.dev',
  displayName: 'Dev User',
  emailVerified: true,
} as unknown as FirebaseUser;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!IS_FIREBASE_CONFIGURED) {
      // No Firebase credentials — auto-sign in as a local dev user so the app
      // can be developed and tested without setting up a Firebase project.
      setUser(DEV_USER);
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  async function signOut() {
    // Clear session-scoped flags so the next login starts fresh.
    sessionStorage.removeItem('iomi_subscription_skipped');

    if (!IS_FIREBASE_CONFIGURED) {
      // Dev mode: no real session to clear. Reload so the dev user is restored.
      window.location.reload();
      return;
    }
    await firebaseSignOut(auth);
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
